import { ReactNode } from 'react';

type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info';
type BadgeSize = 'sm' | 'md';

interface BadgeProps {
  children: ReactNode;
  variant?: BadgeVariant;
  size?: BadgeSize;
  icon?: ReactNode;
  className?: string;
}

const variantStyles: Record<BadgeVariant, string> = {
  default: 'bg-space-600 text-gray-300 border-cosmic-purple/20',
  success: 'bg-green-500/20 text-green-400 border-green-500/30',
  warning: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  danger: 'bg-red-500/20 text-red-400 border-red-500/30',
  info: 'bg-cosmic-blue/20 text-cosmic-blue border-cosmic-blue/30',
};

const sizeStyles: Record<BadgeSize, string> = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-3 py-1 text-sm',
};

export function Badge({
  children,
  variant = 'default',
  size = 'sm',
  icon,
  className = '',
}: BadgeProps) {
  return (
    <span
      className={`
        inline-flex items-center gap-1
        font-medium rounded-full border
        ${variantStyles[variant]}
        ${sizeStyles[size]}
        ${className}
      `}
    >
      {icon}
      {children}
    </span>
  );
}

/**
 * Conservation status badge
 * LC = Least Concern, NT = Near Threatened, VU = Vulnerable,
 * EN = Endangered, CR = Critically Endangered, EX = Extinct
 */
export function ConservationBadge({ status }: { status: string }) {
  const statusConfig: Record<string, { label: string; variant: BadgeVariant }> = {
    LC: { label: 'Вне опасности', variant: 'success' },
    NT: { label: 'Близки к уязвимому', variant: 'info' },
    VU: { label: 'Уязвимый', variant: 'warning' },
    EN: { label: 'Под угрозой', variant: 'danger' },
    CR: { label: 'На грани вымирания', variant: 'danger' },
    EX: { label: 'Вымерший', variant: 'default' },
  };

  const config = statusConfig[status] || { label: status, variant: 'default' as BadgeVariant };

  return <Badge variant={config.variant}>{config.label}</Badge>;
}
